import { computed, ref, toValue, watch, type MaybeRefOrGetter, type Ref} from 'vue'
import type { Beam, Cell, Cluster } from './beamsTypes.ts'
import constrain from '@nicolaudie-group/ng-ui-kit/helpers/constrain'
import { beamHelpers } from '@/helpers/profile'
import { useWritableRef } from '@/composables/useWritableRef'
import translate from '@/helpers/translate'
import useTranslate from '@/composables/useTranslate'
import clusterColors from './_cluster-colors.module.scss'
import { useArrayToReactiveMap } from '@/composables/useArrayToReactiveMap'

/**
 * Minimum size of the grid
 */
const MIN_ALLOWED_GRID_SIZE = 3
/**
 * Maximum size of the grid
 */
const MAX_ALLOWED_GRID_SIZE = 50

export function useBeamsGridSize(beams: MaybeRefOrGetter<Beam[]>) {

  /**
   * Width of the grid in columns as defined by the component BeamsGridSize
   */
  const manualColumns = ref(MIN_ALLOWED_GRID_SIZE)
  /**
   * Height of the grid in rows as defined by the component BeamsGridSize
   */
  const manualRows = ref(MIN_ALLOWED_GRID_SIZE)

  /**
   * Number of columns and rows that have beams
   */
  const occupied = computed(() => {
    return beamHelpers.countOccupied(toValue(beams))
  })

  /**
   * Number of columns that should be displayed on the grid
   */
  const columns = computed(() => constrain(
    MIN_ALLOWED_GRID_SIZE,
    Math.max(occupied.value.columns, manualColumns.value),
    MAX_ALLOWED_GRID_SIZE,
  ))

  /**
   * Number of rows that should be displayed on the grid
   */
  const rows = computed(() => constrain(
    MIN_ALLOWED_GRID_SIZE,
    Math.max(occupied.value.rows, manualRows.value),
    MAX_ALLOWED_GRID_SIZE,
  ))

  /**
   * Minimum width of the grid, counting occupied columns
   */
  const minColumns = computed(() => {
    return Math.max(occupied.value.columns + 1, MIN_ALLOWED_GRID_SIZE)
  })

  /**
   * Minimum height of the grid, counting occupied columns
   */
  const minRows = computed(() => {
    return Math.max(occupied.value.rows + 1, MIN_ALLOWED_GRID_SIZE)
  })

  /**
   * Change the size of the grid
   */
  function gridSizeChanged(columns: number, rows: number) {
    if (columns >= occupied.value.columns + 1) {
      manualColumns.value = columns
    } else {
      manualColumns.value = Math.min(occupied.value.columns + 1, MAX_ALLOWED_GRID_SIZE)
    }
    if (rows >= occupied.value.rows + 1) {
      manualRows.value = rows
    } else {
      manualRows.value = Math.min(occupied.value.rows + 1, MAX_ALLOWED_GRID_SIZE)
    }
  }

  /**
   * Trigger the change of the grid size
   * when the beams change
   */
  watch(beams, () => {
    gridSizeChanged(columns.value, rows.value)
  }, { immediate: true })

  return {
    minColumns,
    maxColumns: MAX_ALLOWED_GRID_SIZE,
    columns,
    minRows,
    maxRows: MAX_ALLOWED_GRID_SIZE,
    rows,
    gridSizeChanged,
  }
}

export function useBeamsBaseEditor(initialBeams: MaybeRefOrGetter<Beam[]>) {

  const beams = useWritableRef(initialBeams)

  /**
   * Add a beam at a cell's position
   */
  function addBeam(column: number, row: number, data?: Partial<Beam>) {
    const beamsValue = beams.value
    const beamsLength = beamsValue.length
    let newBeam: Beam | undefined

    // Limit the number of beams to 512 since this is the maximum that
    // can fit on 1 universe
    if (beamsLength < 512) {
      newBeam = {
        id: beamsLength + 1,
        pos_x: column,
        pos_y: row,
        ...data,
      }

      beams.value = [...beamsValue, newBeam]
    }

    return newBeam
  }

  /**
   * Remove a beam from the list and change the ids of the following beams
   */
  function removeBeam(beamToRemove: Pick<Beam, 'id'>) {
    const beamIdToRemove = beamToRemove.id
    const result: Beam[] = []
    let removed: Beam | undefined

    beams.value.forEach((beam) => {
      const beamId = beam.id
      if (beamId === beamIdToRemove) removed = beam
      // Change the ids of the following beams so that they stay continuous
      // If the beam was after the removed beam
      // Decrease the beam id by 1 (the amount of beams removed)
      else result.push(beamId < beamIdToRemove ? beam : { ...beam, id: beamId - 1 })
    })

    beams.value = result
    return removed
  }

  function editBeam<B extends Pick<Beam, 'id'>>(data: B) {
    let editedBeam: (Beam & B) | undefined
    beams.value = beams.value.map((beam) => {
      if (beam.id !== data.id) return beam
      editedBeam = {
        ...beam,
        ...data,
      }
      return editedBeam
    })
    return editedBeam
  }

  /**
   * Toggle the beam on a cell
   */
  function toggleBeam(cell: Cell, cellChecked: boolean) {
    if (cellChecked && !cell.beam) {
      // If the cell is now checked, since we're in assignment mode,
      // create a beam at that cell's position
      addBeam(cell.column, cell.row)
    } else if (cell.beam) {
      // If the cell is now unchecked, since we're in assignment mode,
      // remove the beam of that cell
      removeBeam(cell.beam)
    }
  }

  return {
    beams,
    addBeam,
    removeBeam,
    editBeam,
    toggleBeam,
  }
}

interface ClusterOption {
  value: number;
  label: string;
  asset: string | undefined;
}

const NO_CLUSTER_ID = -1

/**
 * Maximum number of clusters a profile can have, sourced from
 * `$count` in `_cluster-constants.scss` via the `:export` block
 * in `_cluster-colors.module.scss`.
 */
export const MAX_CLUSTERS = Number(clusterColors.count)


function generateCircleSvg(color: string) {
  return `<svg width="100%" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <circle r="45" cx="50" cy="50" fill="${color}" />
</svg>`
}

function getClusterNumber(clusterId: MaybeRefOrGetter<number>) {
  return String(toValue(clusterId) + 1).padStart(2, '0')
}

function getFallbackClusterName(clusterIdOrNumber: MaybeRefOrGetter<number | string>) {
  const value = toValue(clusterIdOrNumber)
  const clusterNumber = typeof value === 'string' ? value : getClusterNumber(value)
  return translate('pb2.beamsEditor.defaultClusterName').template({ number: clusterNumber })
}

interface ClusterStaticOption {
  value: number;
  clusterNumber: string;
  asset: string | undefined;
}

/**
 * Color/SVG asset per cluster, computed once since `clusterColors` is static
 * and never changes at runtime.
 */
const CLUSTER_STATIC_OPTIONS: ClusterStaticOption[] = Array.from({ length: MAX_CLUSTERS }, (_, i) => {
  const clusterNumber = getClusterNumber(i)
  const clusterColor = clusterColors[`color-${clusterNumber}`]
  return {
    value: i,
    clusterNumber,
    asset: clusterColor && generateCircleSvg(clusterColor),
  }
})

export function useBeamsClusterEditor(initialBeams: Ref<Beam[]>, initialClusters: Ref<Cluster[]>) {

  const { translate } = useTranslate()

  const clustersMap = useArrayToReactiveMap(
    initialClusters,
    'id',
    (item) => item.name,
    (item, key) => ({ id: key, name: item }),
  )

  const clusterId = ref(NO_CLUSTER_ID)

  const {
    beams,
    addBeam,
    removeBeam,
    editBeam,
    toggleBeam,
  } = useBeamsBaseEditor(initialBeams)

  /**
   * Gets the beams of the profile, and add a "selected" flag on them
   * if their "id" matches a "beam_index" in the provided list of selected
   * beams.
   *
   * Sets the list of selected beams based on the "selected" flag from
   * the list of beams of the profiles returned by the getter
   */
  const clusterBeams = computed(() => {
    const profileBeams = toValue(beams)
    const clusterIdValue = clusterId.value

    return profileBeams.map((beam) => {
      const selected = clusterIdValue === beam.clusterId || clusterIdValue === NO_CLUSTER_ID
      return {
        ...beam,
        selected,
        disabled: false,
      }
    })
  })

  const clusterOptions = computed(() => {
    const group: ClusterOption[] = CLUSTER_STATIC_OPTIONS.map(({ value, clusterNumber, asset }) => ({
      value,
      label: clustersMap.get(value) || getFallbackClusterName(clusterNumber),
      asset,
    }))

    const options = [
      {
        value: NO_CLUSTER_ID,
        label: translate('pb2.beamsEditor.addOrRemoveBeamsOptionLabel'),
      },
      {
        label: translate('pb2.beamsEditor.editClusterGroupLabel'),
        group,
      }
    ]

    return options
  })

  const clusterIsSelected = computed(() => clusterId.value >= 0)

  const defaultClusterName = computed(() => getFallbackClusterName(clusterId))

  const selectedClusterName = computed({
    get() {
      return clustersMap.get(clusterId.value) ?? ''
    },
    set(name: string) {
      const clusterIdValue = clusterId.value
      if (clusterIdValue < 0) return
      if (name) clustersMap.set(clusterIdValue, name)
      else clustersMap.delete(clusterIdValue)
    }
  })

  /**
   * Toggle select flag on one beam
   * @param cell - Cell containing beam to toggle, toggle value
   */
  function toggleBeamCluster(cell: Cell, cellChecked: boolean) {
    const clusterIdValue = clusterId.value

    // If we're haven't selected a cluster to edit, simply toggle the beam
    if (clusterIdValue < 0) {
      return toggleBeam(cell, cellChecked)
    }

    // Otherwise if we've selected a cluster to edit

    // Get the beam of the cell and compute it's new cluster ID
    const clickedBeam = cell.beam
    const newClusterId = cellChecked && clusterIdValue >= 0 ? clusterIdValue : undefined

    // If the beam does not exist, create it with the newly computed cluster ID
    if (!clickedBeam) {
      return addBeam(cell.column, cell.row, { clusterId: newClusterId })
    }

    // If the beam exists and the cluster ID is being changed, update it
    if (newClusterId !== clickedBeam.clusterId) {
      return editBeam({ id: clickedBeam.id, clusterId: newClusterId })
    }

    return undefined
  }

  return {
    clusterBeams,
    clusterOptions,
    clusterId,
    clusterIsSelected,
    defaultClusterName,
    selectedClusterName,
    toggleBeamCluster,
    toggleBeam,
    addBeam,
    removeBeam,
  }

}
